import React, { useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { FiUpload, FiCheck, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Loading from '../../ions/Loading';
import { contractApi } from '../../../services/contractApi';
import UploadStep from './UploadStep';
import ExtractionStep from './ExtractionStep';
import ConfirmStep from './ConfirmStep';

type Step = 'upload' | 'extraction' | 'confirm';

const STEPS: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: 'upload', label: 'Upload', icon: <FiUpload size={14} /> },
    { key: 'extraction', label: 'Extração', icon: <FiCheck size={14} /> },
    { key: 'confirm', label: 'Confirmação', icon: <FiCheck size={14} /> },
];

const ContractCreatePage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('upload');
    const [loading, setLoading] = useState(false);

    const [documentId, setDocumentId] = useState('');
    const [contractId, setContractId] = useState('');
    const [fileName, setFileName] = useState('');
    const [operationNumber, setOperationNumber] = useState('');
    const [extractedData, setExtractedData] = useState<Record<string, unknown> | null>(null);
    const [iterationNumber, setIterationNumber] = useState(0);

    const handleUploadComplete = async (docId: string, cId: string, fName: string, opNumber: string) => {
        setDocumentId(docId);
        setContractId(cId);
        setFileName(fName);
        setOperationNumber(opNumber);

        // Auto-extract after upload
        setLoading(true);
        try {
            const result = await contractApi.createExtraction(docId, cId);
            const extraction = await contractApi.getExtractionPageForm(result.extractionId);
            setExtractedData(extraction.extractedData);
            setIterationNumber(extraction.iterationNumber);
            setStep('extraction');
        } catch (e: any) {
            toast.error(e.message || 'Erro na extração');
        } finally {
            setLoading(false);
        }
    };

    const stepIndex = STEPS.findIndex(s => s.key === step);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            {loading && <Loading />}

            <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
                <div className="flex items-center gap-3 max-w-4xl mx-auto">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                        <FiArrowLeft className="text-gray-600 dark:text-gray-400" />
                    </button>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Novo Contrato</h1>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-4">
                <div className="flex gap-1">
                    {STEPS.map((s, i) => (
                        <div key={s.key} className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm rounded-lg transition-colors ${
                            i < stepIndex ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                            i === stepIndex ? 'bg-blue-600 text-white' :
                            'bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-gray-500'
                        }`}>
                            {s.icon} {s.label}
                        </div>
                    ))}
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 pb-8">
                {step === 'upload' && <UploadStep onComplete={handleUploadComplete} setLoading={setLoading} />}
                {step === 'extraction' && (
                    <ExtractionStep
                        documentId={documentId} contractId={contractId}
                        onComplete={() => setStep('confirm')} onBack={() => setStep('upload')}
                        setLoading={setLoading}
                        extractedData={extractedData} setExtractedData={setExtractedData}
                        iterationNumber={iterationNumber} setIterationNumber={setIterationNumber}
                    />
                )}
                {step === 'confirm' && (
                    <ConfirmStep
                        contractId={contractId} extractedData={extractedData}
                        onComplete={() => navigate('/contract/create/send')}
                        onBack={() => setStep('extraction')}
                        setLoading={setLoading}
                    />
                )}
                <Outlet context={{ contractId, documentId, operationNumber, fileName }} />
            </div>
        </div>
    );
};

export default ContractCreatePage;
